import {Component, Element, Event, EventEmitter, h, Host, Method, Prop, Watch} from '@stencil/core'
import {boolToFocusValue, govHost, toBoolAttr} from '../../helpers/Dom/template'
import {GovHost} from '../../helpers/Dom/template.types'
import {canValidateWcagOnRender} from '../../helpers/Dom/win'
import {validateWcagRef} from '../../helpers/Validate/wcag'
import {Nullable} from '../../types/interfaces'
import {createID} from '../../utils/string.utils'
import {DialogClass} from './constants'
import {tick} from "../../utils/stencil.utils";
import {getFocusableElements} from "../../utils/dom.utils";

@Component({
	tag: 'gov-dialog',
})
export class GovDialog {
	private h: GovHost
	private readonly labelId: string
	private readonly contentId: string
	private hydrated = false;
	private closeCtaRef?: HTMLGovButtonElement
	private dialogRef?: HTMLDialogElement;
	private lastActiveElementRef?: HTMLElement;

	constructor() {
		this.h = govHost(this.host)
		this.labelId = createID('GovDialogLabel')
		this.contentId = createID('GovDialogContent')
	}

	@Element() host: HTMLGovDialogElement

	/**
	 * Attribute for dialog window display
	 */
	@Prop({mutable: true, reflect: true}) open = false
	/**
	 * Attribute allowing blocking the dialog closing
	 */
	@Prop({attribute: 'block-close'}) blockClose = false
	/**
	 * Attribute allowing blocking the backdrop dialog closing
	 */
	@Prop({attribute: 'block-backdrop-close'}) blockBackdropClose = false
	/**
	 * Role of dialog
	 */
	@Prop({attribute: 'role'}) role: Nullable<string> = 'dialog'
	/**
	 * Used to change the HTML tag in the dialog label for correct semantic structure
	 */
	@Prop({attribute: 'label-tag'}) readonly labelTag: string = 'h2'
	/**
	 * String of id's that indicates alternative labels elements
	 */
	@Prop({attribute: 'accessible-labelled-by'}) accessibleLabelledBy: string
	/**
	 * Indicates the id of a component that describes the dialog.
	 */
	@Prop({attribute: 'accessible-described-by'}) accessibleDescribedBy: string
	/**
	 * Adds accessible label for the dialog close button that is only shown for screen readers.
	 */
	@Prop({attribute: 'accessible-close-label'}) readonly accessibleCloseLabel: string = 'Zavřít okno'
	/**
	 * A string of identifiers that indicate alternative label elements for closing the dialog window
	 */
	@Prop({attribute: 'accessible-close-labelled-by'}) accessibleCloseLabelledBy: string
	/**
	 * Emitted when a dialog window closes.
	 */
	@Event({eventName: 'gov-close'}) govClose: EventEmitter<PointerEvent>

	@Watch('open')
	watchOpen(newValue: boolean): void {
		this.open = newValue
		this.setFocus().finally()

		if (!newValue) {
			this.lastActiveElementRef?.focus();
		} else {
			this.focusTrap();
		}
	}

	private showDialog() {
		this.open = true
		document.body.classList.add(DialogClass.bodyFix)
	}

	private hideDialog() {
		this.open = false
		document.body.classList.remove(DialogClass.bodyFix)
	}

	async componentDidRender() {
		if (canValidateWcagOnRender()) {
			tick(() => this.validateAccessibility())
		}

		if (this.open) {
			this.showDialog()
		} else {
			this.hideDialog()
		}
	}

	private focusTrap() {
		if (this.open && this.dialogRef) {
			this.lastActiveElementRef = document.activeElement as HTMLElement;
			const focusableEls = getFocusableElements(this.dialogRef) as HTMLElement[];
			if (focusableEls.length) {
				focusableEls[0].focus();
			}

			const handleKeyDown = (e: KeyboardEvent) => {
				if (e.key === "Tab" && this.dialogRef) {
					const focusableEls = getFocusableElements(this.dialogRef);
					const firstEl = focusableEls[0] as HTMLElement;
					const lastEl = focusableEls[focusableEls.length - 1] as HTMLElement;

					if (!e.shiftKey && document.activeElement === lastEl) {
						e.preventDefault();
						firstEl.focus();
					}
					if (e.shiftKey && document.activeElement === firstEl) {
						e.preventDefault();
						lastEl.focus();
					}
				}
				if (e.key === "Escape") {
					if (this.blockClose === false) {
						this.hideDialog();
						this.govClose.emit();
					}
				}
			};

			document.addEventListener("keydown", handleKeyDown);
		} else {
			this.lastActiveElementRef?.focus();
		}
	}

	componentDidLoad() {
		// `@Watch('open')` fires only on a runtime open transition, so a dialog mounted
		// already-open (e.g. gov-cookiebar reopened from its badge) would otherwise never
		// wire the focus trap. Do it once here when we mount open.
		if (this.open) {
			this.focusTrap()
		}
	}

	connectedCallback() {
		this.hydrated = true
	}

	disconnectedCallback(): void {
		document.body.classList.remove(DialogClass.bodyFix)
	}

	render() {
		return (
			<Host class={this.h.classes(DialogClass.root)} open={this.open} hydrated={this.hydrated}>
				<dialog
					ref={el => (this.dialogRef = el)}
					class={DialogClass.dialog}
					hidden={!this.open}
					tabindex={boolToFocusValue(this.open)}
					open={this.open}
					aria-modal="true"
					aria-hidden={toBoolAttr(!this.open)}
					aria-labelledby={this.accessibleLabelledBy || this.labelId}
					aria-describedby={this.accessibleDescribedBy || this.contentId}
					role={this.role}
				>
					<div class={DialogClass.inner}>
						<div class={DialogClass.header}>
							{this.h.hasSlot("icon") && (
								<span>
									<slot name="icon"/>
								</span>
							)}
							{this.h.hasSlot('title') && (
								<span class={DialogClass.title} id={this.labelId}>
									<slot name="title"/>
								</span>
							)}
							<gov-button
								ref={el => (this.closeCtaRef = el)}
								disabled={this.blockClose}
								class={DialogClass.close}
								color="primary"
								aria-label={this.accessibleCloseLabel}
								aria-labelledby={this.accessibleCloseLabelledBy}
								type="base"
								size="m"
								on-gov-click={this.onCloseHandler.bind(this)}
							>
								<gov-icon slot="icon-start" name="x-lg" type="components"></gov-icon>
							</gov-button>
						</div>
						<div class={DialogClass.content} id={this.contentId}>
							<slot/>
						</div>
						{this.h.hasSlot('footer') && (
							<div class={DialogClass.footer}>
								<slot name="footer"/>
							</div>
						)}
					</div>
				</dialog>

				{this.open ? (
					<gov-backdrop
						disabled={this.blockBackdropClose}
						accessible-label={this.accessibleCloseLabel}
						accessible-labelledby={this.accessibleCloseLabelledBy}
						on-gov-click={this.onCloseHandler.bind(this)}
					/>
				) : null}
			</Host>
		)
	}

	private onCloseHandler(e: PointerEvent) {
		e.stopPropagation()
		e.preventDefault()
		if (this.blockClose === false) {
			this.hideDialog()
			this.govClose.emit(e)
		}
	}

	/**
	 * Sets the close button to focus
	 */
	@Method()
	async setFocus(): Promise<void> {
		if (this.open && this.closeCtaRef) {
			this.closeCtaRef.setFocus().catch()
		}
	}


	/**
	 * Validate the WCAG attributes of the component
	 */
	@Method()
	async validateAccessibility(): Promise<void> {
		validateWcagRef(this.accessibleLabelledBy, 'accessible-labelled-by', DialogClass.root)
		validateWcagRef(this.accessibleDescribedBy, 'accessible-described-by', DialogClass.root)
	}
}
